import { memo } from "react";
import type { RenderKeycap } from "@/domain/types";
import type { CapStyle } from "@/render/tweaks/use-tweaks";
import { backlitFace, haloPct, pressedSurface } from "./cap-face";
import { nameFontSize } from "./keycap-name";

interface KeycapProps {
  cap: RenderKeycap;
  unit: number;
  showNames?: boolean;
  keycapStyle?: CapStyle;
  /** Keycap art: names (default) or opt-in icon tiles, looked up by Binding.cmd. */
  keycapLabel?: "names" | "icons";
  /** Resolves a command id to its icon-tile SVG markup. Injected by the SC2-aware surface owner so
   * this generic primitive never names a game; only consulted in icons mode. */
  iconFor?: (cmd: string | undefined) => string | null;
  /** Backlight strength 0-1.5; scales the backlit outer halo. */
  keyGlow?: number;
  activeMod?: boolean;
  /** Faded out: isolated category, search/hover flashlight, or held-combo fallback. */
  dim?: boolean;
  /** Pressed: the cap depresses into the plate and fills with the Category accent.
   * The single press-feedback system; the bindings-list click flash folds in here. */
  pressed?: boolean;
  onPress?: (code: string, legend: string) => void;
}

export const Keycap = memo(function Keycap({
  cap,
  unit,
  showNames = true,
  keycapStyle = "backlit",
  keycapLabel = "names",
  iconFor,
  keyGlow = 0.85,
  activeMod = false,
  dim = false,
  pressed = false,
  onPress,
}: KeycapProps) {
  const pad = Math.max(1, unit * 0.07);
  const c = cap.colors;
  const bound = cap.binding != null;
  // the cap legend prefers the short form (the space-constrained surface); the full name stays on the
  // hover title and on the bindings-list/readout. nameFontSize then shrinks THIS string to fit.
  const capName = cap.binding ? (cap.binding.short ?? cap.binding.name) : "";
  const backlit = keycapStyle === "backlit";
  const kg = Math.max(0, keyGlow);
  const w = cap.w * unit - pad * 2;
  const h = (cap.h || 1) * unit - pad * 2;
  // icon mode: the binding's art tile (looked up by its command id), with a text
  // fallback when no icon is mapped. The art owns the face; the legend hides.
  const icon = keycapLabel === "icons" && bound && iconFor ? iconFor(cap.binding?.cmd) : null;
  const stripH = Math.max(3, unit * 0.1);
  const artGlyphSize = Math.max(13, Math.min(w, h - stripH) * 0.6);
  // the Category strip across the cap top; the legend clears it so the char never reads over the bar
  const barH = Math.max(3, unit * 0.08);

  // the cap face: backlit paints a dark RGB-lit cap; the flat styles tint/strip/dot
  // the light default cap. undefined surface falls through to the CSS default.
  let surface: string | undefined;
  let legendColor: string | undefined;
  let nameColor: string | undefined = c?.text;
  let topBar: string | undefined;
  let dot: string | undefined;

  if (backlit) {
    const face = backlitFace(bound, c);
    surface = face.surface;
    legendColor = face.legend;
    if (bound && c) {
      nameColor = c.lit;
      topBar = c.glow;
    } else {
      nameColor = "transparent";
    }
  } else if (bound && c) {
    if (keycapStyle === "tint") surface = c.tint;
    if (keycapStyle === "tint" || keycapStyle === "bar") topBar = c.accent;
    if (keycapStyle === "dot") dot = c.accent;
  }

  // pressing the cap fills it with the Category accent in white (depress)
  if (pressed) {
    surface = pressedSurface(c?.accent, backlit);
    legendColor = "rgba(255,255,255,.92)";
    nameColor = "#fff";
    topBar = undefined;
    dot = undefined;
  }

  // physicality: a visible side wall + the backlit outer halo (scaled by keyGlow);
  // pressing translates the cap down by (wall - 1)px and swaps to an inset shadow
  const wallH = Math.max(3, Math.round(unit * 0.1));
  const litPct = haloPct(kg);
  const keyHalo =
    backlit && bound && c && !dim && litPct > 0
      ? `, 0 0 ${Math.max(8, Math.round(unit * 0.34 * kg))}px ${Math.max(1, Math.round(unit * 0.04 * kg))}px color-mix(in oklch, ${c.glow} ${litPct}%, transparent)`
      : "";
  // the flat styles keep the light CSS .kc wall when raised; backlit sets its own
  const raisedShadow = backlit
    ? `inset 0 1px 0 rgba(255,255,255,.1), 0 ${wallH}px 0 0 oklch(0.175 0.008 261), 0 ${wallH + 4}px ${wallH + 7}px rgba(0,0,0,.42)${keyHalo}`
    : undefined;
  const depressShadow =
    (backlit
      ? "inset 0 2px 5px rgba(0,0,0,.5), 0 1px 0 rgba(0,0,0,.3)"
      : "inset 0 2px 3px rgba(15,18,28,.20), 0 1px 0 rgba(0,0,0,.05)") + keyHalo;
  const backlitBorder = backlit
    ? bound && c
      ? `1px solid color-mix(in oklch, ${c.glow} 28%, rgba(0,0,0,.38))`
      : "1px solid rgba(0,0,0,.35)"
    : undefined;

  // the raised / depressed plate shadow (the flat styles fall through to the CSS .kc wall)
  const boxShadow = pressed ? depressShadow : raisedShadow;

  const originX = ((cap.rx ?? cap.x) - cap.x) * unit - pad;
  const originY = ((cap.ry ?? cap.y) - cap.y) * unit - pad;
  const transform = [
    cap.r ? `rotate(${cap.r}deg)` : "",
    pressed ? `translateY(${Math.max(2, wallH - 1)}px)` : "",
  ]
    .filter(Boolean)
    .join(" ");

  // a rotated cap's screen footprint (axis-aligned bounding box of the rotated rectangle): the
  // counter-rotated text layer is sized to THIS and centered, so upright text fills the cap's
  // visible slot instead of overflowing the narrow rotated rectangle and clipping (a 90deg 1.25u
  // key then reads inside its 1u-wide screen footprint).
  const rad = ((cap.r ?? 0) * Math.PI) / 180;
  const footW = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad));
  const footH = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad));
  // only near-vertical caps (a split board's quarter-turned inner columns) get the upright
  // counter-rotation - their text would otherwise read sideways. A gently-angled key like a thumb
  // cluster reads fine tilted with the cap, so it keeps its natural orientation.
  const spin = Math.abs(cap.r ?? 0) >= 75;

  const classes = ["kc"];
  if (backlit) classes.push("kc-backlit");
  if (activeMod) classes.push("kc-mod-on");
  if (dim) classes.push("kc-dim");
  if (pressed) classes.push("kc-press");

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the keyboard path is the window keydown listener in useReadout; a focusable button per cap would put 100+ stops in the tab order
    <div
      className={classes.join(" ")}
      title={bound ? cap.binding?.name : cap.legend}
      onMouseDown={
        onPress
          ? (e) => {
              if (e.button !== 0) return; // non-left buttons are the physical mouse-button capture
              e.preventDefault();
              // press resolves on the cap's active code; a dead cap has none and reads out nothing
              if (cap.code != null) onPress(cap.code, cap.legend);
            }
          : undefined
      }
      style={{
        left: cap.x * unit + pad,
        top: cap.y * unit + pad,
        width: w,
        height: h,
        borderRadius: Math.max(4, unit * 0.13),
        transform: transform || undefined,
        transformOrigin: cap.r ? `${originX}px ${originY}px` : undefined,
        background: surface,
        boxShadow,
        border: backlitBorder,
      }}
    >
      {/* the cap face. A rotated cap (a split board's inner column / thumb cluster) keeps its true
          geometry, but its whole face - the category bar, legend, and name - lives in this layer,
          which is sized to the cap's upright screen footprint and counter-rotates, so everything
          reads upright and stays inside the visible slot. A non-rotated cap fills the cap as before. */}
      <div
        className="kc-rot"
        style={
          spin
            ? {
                left: "50%",
                top: "50%",
                width: footW,
                height: footH,
                transform: `translate(-50%, -50%) rotate(${-(cap.r ?? 0)}deg)`,
              }
            : { inset: 0 }
        }
      >
        {icon && c ? (
          <>
            <span className="kc-icon-strip" style={{ height: stripH, background: c.accent }} />
            <span
              className="kc-icon-art"
              style={{
                top: stripH,
                color: pressed ? "#fff" : "rgba(233,236,243,.9)",
                background: pressed
                  ? `linear-gradient(158deg, color-mix(in oklch, ${c.accent} 46%, oklch(0.34 0.014 262)), color-mix(in oklch, ${c.accent} 20%, oklch(0.225 0.013 262)))`
                  : "linear-gradient(158deg, oklch(0.352 0.014 262), oklch(0.234 0.013 262))",
              }}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                width={artGlyphSize}
                height={artGlyphSize}
                fill="currentColor"
                style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,.45))" }}
                // biome-ignore lint/security/noDangerouslySetInnerHtml: static vendored glyph markup (sc2-icons), not user input
                dangerouslySetInnerHTML={{ __html: icon }}
              />
            </span>
          </>
        ) : (
          <>
            {topBar ? (
              <span
                className="kc-bar"
                style={{
                  height: barH,
                  background: topBar,
                  opacity: backlit ? 0.95 : 0.9,
                  boxShadow: backlit ? `0 1px 7px ${topBar}` : undefined,
                }}
              />
            ) : null}
            {dot ? (
              <span
                className="kc-dot"
                style={{
                  top: pad + 1,
                  right: pad + 1,
                  width: Math.max(5, unit * 0.12),
                  height: Math.max(5, unit * 0.12),
                  background: dot,
                }}
              />
            ) : null}
            <span
              className="kc-legend"
              style={{
                // clear the Category strip when one is present, so the char never overlaps the bar
                top: topBar ? barH + Math.max(1, unit * 0.04) : undefined,
                fontSize: Math.max(9, unit * (showNames && bound ? 0.2 : 0.26)),
                color: legendColor,
              }}
            >
              {cap.legend}
            </span>
            {bound && showNames ? (
              <span
                className="kc-name"
                style={{
                  fontSize: nameFontSize(capName, w, unit),
                  color: nameColor,
                }}
              >
                {capName}
              </span>
            ) : null}
          </>
        )}
      </div>
      {/* rapid-fire ON: an inset amber ring hugging the cap (replaces the old corner glyph). Drawn as
          an overlay so it layers over whatever box-shadow the cap style computed, and so it never
          collides with the outer ember .kc-mod-on modifier outline. */}
      {cap.rapidFire ? <span className="kc-rf-ring" aria-hidden="true" /> : null}
      {/* the "+N" badge: this cap carries N shadowed (deferred) commands. Top-right; hidden when
          names are off (an icons-only board). */}
      {showNames && cap.binding?.shadowed?.length ? (
        <span
          className="kc-badge"
          style={{ fontSize: Math.max(8, unit * 0.15) }}
          aria-hidden="true"
        >
          +{cap.binding.shadowed.length}
        </span>
      ) : null}
    </div>
  );
});
